import { FC, Suspense, Dispatch, SetStateAction } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';

import { useGateSoldPrice } from '../../../shared/hooks/useGateSoldPrice';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';

// components
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { LocalizedSoldIcon } from 'dibs-elements/exports/LocalizedSoldIcon';
import { LocalizedUnavailableIcon } from 'dibs-elements/exports/LocalizedUnavailableIcon';

// styles
import styles from './PdpRespUnavailableHeader.scss';

// types
import { PdpRespUnavailableHeader_item$key as Item } from './__generated__/PdpRespUnavailableHeader_item.graphql';
import { PdpRespUnavailableHeader_user$key as User } from './__generated__/PdpRespUnavailableHeader_user.graphql';

const SharedGateViewPrice = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "ViewPriceToast" */ '../../../shared/SharedGateViewPrice/SharedGateViewPrice'
        )
);

const PdpSharedContactSellerButton = dibsLazy(
    'PdpUnavailableSellerAccessible',
    () =>
        import(
            /* webpackChunkName: "PdpUnavailableSellerAccessible" */ '../../shared/PdpSharedContactSellerButton'
        )
);

const userFragment = graphql`
    fragment PdpRespUnavailableHeader_user on User {
        ...PdpSharedContactSellerButton_user
        ...SharedGateViewPrice_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespUnavailableHeader_item on Item {
        ...PdpSharedContactSellerButton_item
        ...SharedGateViewPrice_item
        ...useGateSoldPrice_item
        title
        isSold
        photos {
            smallPath
        }
        seller {
            isAccessible
        }
    }
`;

export const PdpRespUnavailableHeader: FC<{
    item: Item;
    user: User | null | undefined;
    setViewPriceToast: Dispatch<SetStateAction<boolean>>;
}> = ({ item: itemRef, user: userRef, setViewPriceToast }) => {
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const { currency } = useRespUnavailableContext();
    const { isSold, seller } = item;
    const LocalizedIcon = isSold ? LocalizedSoldIcon : LocalizedUnavailableIcon;
    const shouldGateSoldPrice = useGateSoldPrice(item, currency);

    return (
        <div className={styles.container} data-tn="pdp-dlos-heading-info">
            <div className={styles.imageContainer}>
                <img
                    src={item.photos?.[0]?.smallPath || ''}
                    alt={item.title || ''}
                    className={styles.image}
                />
            </div>

            <div className={styles.content}>
                <ClientSuspense fallback={<div className={styles.soldIcon} />}>
                    {shouldGateSoldPrice ? (
                        <SharedGateViewPrice
                            item={item}
                            user={user}
                            currency={currency}
                            setViewPriceToast={setViewPriceToast}
                            launchOrigin="sold gating pdp header"
                        />
                    ) : (
                        <LocalizedIcon className={styles.soldIcon} />
                    )}
                </ClientSuspense>
                <HeadingLevel>
                    {Heading => (
                        <Heading className={styles.title} dataTn="pdp-dlos-heading-title">
                            {item.title}
                        </Heading>
                    )}
                </HeadingLevel>
            </div>
            {seller?.isAccessible && (
                <Suspense fallback={null}>
                    <div className={styles.buttonContainer}>
                        <PdpSharedContactSellerButton
                            user={user}
                            item={item}
                            size="medium"
                            type="transparent"
                            currency={currency}
                            launchOrigin="contact link in sold pdp - topnav"
                        >
                            <FormattedMessage
                                defaultMessage="Message the Seller"
                                id="PdpRespUnavailableHeader.messageTheSeller"
                            />
                        </PdpSharedContactSellerButton>
                    </div>
                </Suspense>
            )}
        </div>
    );
};
